/**
 * The one path for a runtime analytics-consent grant, and the one list of
 * boot-time stashes to replay once analytics can capture.
 *
 * `track()` drops anything fired before `safeAnalytics.init()` or before
 * consent, so boot-time events are stashed in sessionStorage and flushed later
 * (rules/analytics.md, "Track every new user-facing feature" item 4). Every
 * moment that gate can open must flush, and a grant surface that forgot to was
 * how Settings lost the stashes the banner delivered. So both grant surfaces
 * call `grantAnalyticsConsent`, and init and the region default call
 * `flushPendingAnalyticsEvents`; a new stash is added to that list once.
 *
 * Lives outside `cookieConsent.ts` because `analytics.ts` imports that module,
 * and these flushes import `analytics.ts`.
 *
 * @compliance GDPR Article 7 — Conditions for consent
 */

import { ANALYTICS_EVENTS } from '../constants/analytics';
import { safeAnalytics } from './analytics';
import { flushPendingAuthPersistenceRecovery } from './authPersistenceRecovery';
import { acceptAllCookies } from './cookieConsent';
import { flushPendingCspViolations } from './cspViolationTelemetry';
import { flushPendingEmailCta } from './emailCtaCapture';
import { flushPendingRecordingRecoveryOffered } from './recordingRecoveryTelemetry';

/**
 * Replay every stashed boot-time event. Each flush keeps its stash while
 * analytics cannot capture, so calling this before consent sends nothing and
 * loses nothing.
 */
export function flushPendingAnalyticsEvents(): void {
  flushPendingAuthPersistenceRecovery();
  flushPendingRecordingRecoveryOffered();
  flushPendingEmailCta();
  flushPendingCspViolations();
}

/**
 * Record an explicit "allow analytics" choice and act on it now: store it,
 * upgrade the SDK (which re-identifies after a prior reset), track the grant,
 * then replay what was stashed while capture was off. Storing comes first so
 * nothing is sent on a write that throws.
 */
export function grantAnalyticsConsent(source?: 'settings'): void {
  acceptAllCookies();
  safeAnalytics.enableAnalyticsAfterConsent();
  safeAnalytics.track(ANALYTICS_EVENTS.COOKIE_CONSENT_GIVEN, {
    analytics: true,
    functional: true,
    ...(source && { source }),
  });
  flushPendingAnalyticsEvents();
}
